import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { AlertController, IonButton, IonIcon, IonicSafeString, LoadingController } from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IonicScrollManagerService } from '@okcargo-apps/layout-ionic';
import { MessagesUiService } from '@okcargo-apps/messages-ui';
import { IVehicle } from '@okcargo-apps/shared-domain';
import { VehiclesMutationsService } from '../vehicles-mutations.service';
import { VEHICLES_LIST_MESSAGE_OBJECT_ID } from '../vehicles-store.model';
import { VehiclesStoreService } from '../vehicles-store.service';

@Component({
  selector: 'lib-vehicle-delete-action',
  templateUrl: './vehicle-delete-action.component.html',
  styleUrls: ['./vehicle-delete-action.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonButton, IonIcon, TranslateModule],
})
export class VehicleDeleteActionComponent {
  public readonly vehicle = input.required<IVehicle>();

  private readonly alertCtrl = inject(AlertController);
  private readonly loadingCtrl = inject(LoadingController);
  private readonly translate = inject(TranslateService);
  private readonly messagesUiService = inject(MessagesUiService);
  private readonly ionicScrollManagerService = inject(IonicScrollManagerService);
  private readonly vehiclesStoreService = inject(VehiclesStoreService);
  private readonly vehiclesMutationsService = inject(VehiclesMutationsService);

  public async confirmDelete(): Promise<void> {
    const vehicle = this.vehicle();
    const alert = await this.alertCtrl.create({
      header: this.translate.instant('general.attention'),
      message: new IonicSafeString(this.translate.instant('vehicles.are_you_sure_delete')),
      buttons: [
        { text: this.translate.instant('general.cancel'), role: 'cancel' },
        {
          text: this.translate.instant('general.confirm_delete'),
          role: 'destructive',
          handler: () => this.deleteVehicle(vehicle),
        },
      ],
    });

    await alert.present();
  }

  private async deleteVehicle(vehicle: IVehicle): Promise<void> {
    const loading = await this.loadingCtrl.create({
      message: this.translate.instant('general.send_data'),
    });
    await loading.present();

    try {
      await this.vehiclesMutationsService.softDeleteVehicle(vehicle.id);

      this.messagesUiService.setMessage({
        objectId: VEHICLES_LIST_MESSAGE_OBJECT_ID,
        message: [this.translate.instant('vehicles.deleted_message', { plate: vehicle.plateNumber })],
      });

      loading.message = this.translate.instant('general.loading_data');
      await this.vehiclesStoreService.reloadAfterSaveData();
    } catch (error) {
      console.warn(error);
    } finally {
      await loading.dismiss();
    }

    setTimeout(() => this.ionicScrollManagerService.scrollToTop(), 300);
  }
}
